import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Ticket, Bot, User as UserIcon, ClipboardEdit } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const SOURCE_META = {
  "Manuali (admin)": { icon: ClipboardEdit, color: "from-blue-400 to-blue-500", iconColor: "text-blue-500" },
  "Save Cart V1 (admin trigger)": { icon: UserIcon, color: "from-purple-400 to-purple-500", iconColor: "text-purple-500" },
  "Save Cart V1 (scheduler auto)": { icon: Bot, color: "from-emerald-400 to-emerald-500", iconColor: "text-emerald-500" },
};

/**
 * Admin widget — coupons overview: distribution by source (manual / save_cart V1 / scheduler auto)
 * + save_cart redemption rate. Useful to measure: are our auto-generated coupons actually being used?
 */
export const CouponsOverviewWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${API}/dashboard/coupons-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.warn("[coupons-overview] fetch failed:", err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!data || data.total_coupons === 0) {
    return (
      <Card data-testid="coupons-overview-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-amber-500" />
            Coupons — Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm text-center py-4">
            Nessun coupon ancora creato. Crea coupon dall'area Promo o aspetta che il scheduler invii la prima reminder Save Cart.
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...data.by_source.map((s) => s.count), 1);

  return (
    <Card data-testid="coupons-overview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Ticket className="w-5 h-5 text-amber-500" />
          Coupons — Overview
          <div className="ml-auto flex items-center gap-2">
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
              Totale: {data.total_coupons}
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              Attivi: {data.active}
            </Badge>
            {data.expired > 0 && (
              <Badge variant="secondary">Scaduti/disattivati: {data.expired}</Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" data-testid="coupons-by-source">
          {data.by_source.map((s) => {
            const meta = SOURCE_META[s.source] || { icon: Ticket, color: "from-gray-300 to-gray-400", iconColor: "text-gray-500" };
            const Icon = meta.icon;
            const pct = (s.count / maxCount) * 100;
            return (
              <div key={s.source} data-testid={`coupon-source-${s.source}`}>
                <div className="flex justify-between text-sm mb-1 items-center gap-2">
                  <span className="text-gray-700 flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${meta.iconColor}`} />
                    {s.source}
                  </span>
                  <span className="text-gray-900 font-semibold">{s.count}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div
                    className={`bg-gradient-to-r ${meta.color} h-2.5 rounded-full transition-all duration-500`}
                    style={{ width: `${Math.max(pct, 3)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {data.save_cart_total_generated > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-200" data-testid="save-cart-redemption">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 flex items-center gap-2">
                <Bot className="w-4 h-4 text-emerald-500" />
                Save Cart redemption rate
              </span>
              <span
                className={`font-semibold ${
                  data.save_cart_redemption_rate_percent >= 10
                    ? "text-emerald-600"
                    : data.save_cart_redemption_rate_percent >= 5
                      ? "text-amber-600"
                      : "text-gray-700"
                }`}
              >
                {data.save_cart_redemption_rate_percent}% ({data.save_cart_redeemed}/{data.save_cart_total_generated})
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Quanti coupon V1 da carrelli abbandonati vengono effettivamente usati al checkout.
            </p>
          </div>
        )}

        <div className="mt-3 text-xs text-gray-500">
          <strong>{data.used_at_least_once}</strong> coupon usato almeno una volta · <strong>{data.total_redemptions}</strong> redemption totali
        </div>
      </CardContent>
    </Card>
  );
};
